<script setup lang="ts">
import { FolderPlus, X } from 'lucide-vue-next'
import type { CreateFolderRequest } from '~/types/web-chat'

const props = defineProps<{ request: CreateFolderRequest }>()

const emit = defineEmits<{
  'confirm-create-folder': []
  'cancel-create-folder': []
}>()

const { t } = useI18n()

/**
 * A folder the agent proposes, created only if the user accepts (MAR-988: it used to be an ungated
 * server write that ran before any card). The settled line reads `request.outcome`, what the create
 * actually did: `status === 'confirmed'` is set before it runs, so on its own it is not success.
 */
const settledLine = computed(() => {
  const { status, outcome } = props.request
  if (status === 'cancelled') return { text: t('webChat.confirmCards.createFolder.cancelled'), failed: false }
  if (!outcome) return { text: t('webChat.confirmCards.createFolder.working'), failed: false }
  if (outcome.ok) return { text: t('webChat.confirmCards.createFolder.done'), failed: false }
  return { text: t('webChat.confirmCards.createFolder.failed'), failed: true }
})
</script>

<template>
  <div class="mt-3 rounded-lg border border-primary/20 bg-primary-lighter p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 h-5 w-5 rounded-full bg-primary-light flex items-center justify-center shrink-0">
        <FolderPlus class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('webChat.confirmCards.createFolder.title') }}</p>
        <i18n-t keypath="webChat.confirmCards.createFolder.body" tag="p" class="mt-0.5 text-xs text-muted-foreground leading-relaxed" scope="global">
          <template #name>
            <span class="font-medium text-foreground">"{{ request.name }}"</span>
          </template>
          <template #path>
            <span class="font-medium text-foreground">{{ request.path }}</span>
          </template>
        </i18n-t>
        <div
          v-if="request.status === 'pending'"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <Button
            type="button"
            size="sm"
            class="gap-1.5"
            :disabled="!request.name"
            @click="emit('confirm-create-folder')"
          >
            <FolderPlus class="h-3 w-3" />
            {{ t('webChat.confirmCards.createFolder.action') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent-lighter hover:text-foreground transition-colors"
            @click="emit('cancel-create-folder')"
          >
            <X class="h-3 w-3" />
            {{ t('webChat.confirmCards.cancel') }}
          </button>
        </div>
        <p
          v-else
          class="mt-2 text-[11px]"
          :class="settledLine.failed ? 'text-destructive' : 'text-muted-foreground'"
        >
          {{ settledLine.text }}
        </p>
      </div>
    </div>
  </div>
</template>
